import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { post, put } from "@/lib/api";
import type { CreateOutput } from "@/lib/types";
import { AddressFields, emptyAddress, fetchPlace, isAddressFilled, Suggestions, useSuggest, type Address, type Customer } from "./shared";

export function CustomerLinkDialog({ customer, onClose }: { customer: Customer | null; onClose: () => void }) {
  const qc = useQueryClient();
  const session = useMemo(() => crypto.randomUUID(), [customer]);
  const [q, setQ] = useState("");
  const [address, setAddress] = useState<Address>(emptyAddress);
  useEffect(() => { setQ(customer?.name ?? ""); setAddress(emptyAddress); }, [customer]);
  const suggest = useSuggest(q, session);

  const done = (msg: string) => {
    qc.invalidateQueries({ queryKey: ["customers"] });
    qc.invalidateQueries({ queryKey: ["companies"] });
    qc.invalidateQueries({ queryKey: ["company"] });
    toast.success(msg);
    onClose();
  };
  const link = useMutation({
    mutationFn: (c: { id: number; name: string }) => put(`/api/customers/${customer!.id}/sevdesk-contact`, { sevdeskContactId: c.id }).then(() => c),
    onSuccess: (c) => done(`Mit „${c.name}“ verknüpft`),
    onError: (e) => toast.error(e.message),
  });
  const create = useMutation({
    mutationFn: () => post<CreateOutput>(`/api/customers/${customer!.id}/sevdesk-contact`, isAddressFilled(address) ? { address } : {}),
    onSuccess: (res) => { for (const w of res.warnings) toast.warning(w); done("sevDesk-Kontakt angelegt und verknüpft"); },
    onError: (e) => toast.error(e.message),
  });

  async function pickPlace(placeId: string) {
    try {
      const d = await fetchPlace(placeId, session);
      setAddress({ street: d.street, zip: d.zip, city: d.city, countryCode: d.countryCode || "AT" });
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <Dialog open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>{customer?.name} mit sevDesk verknüpfen</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="sevDesk-Kontakt suchen…" value={q} onChange={(e) => setQ(e.target.value)} />
          <Suggestions data={suggest.data} onContact={(c) => link.mutate(c)} onPlace={pickPlace} />
          <div className="space-y-2 border-t pt-3">
            <p className="text-sm font-medium">Oder neuen sevDesk-Kontakt anlegen</p>
            <AddressFields value={address} onChange={setAddress} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button variant="secondary" disabled={create.isPending} onClick={() => create.mutate()}>Kontakt anlegen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
